import { useCallback, useEffect, useState } from 'react';

import { useAuthContext } from '../../../context/AuthContext';
import { useProductRecognition } from '../../../hooks/useProductRecognition';
import { ApiService } from '../../../services/api';
import {
  ClassificationType,
  ProductRecognitionBBox,
  ProductRecognitionCropScanResponse,
} from '../../../types';

export interface OutsSelectionEmbeddingUpdate {
  syntheticId: number;
  bbox: [number, number, number, number];
  barcode: string;
  normalizedUpc?: string;
  productId?: number;
}

function mapBBoxToCropScan(
  bbox: ProductRecognitionBBox,
  index: number,
  classificationType: ClassificationType,
  idOffset: number
): ProductRecognitionCropScanResponse {
  return {
    product_crop_scan_id: idOffset + index + 1,
    order: index,
    classification_type: classificationType,
    orientation_class: 'front',
    valuable_to_classify: true,
    bbox: bbox.shape,
    points: [],
  };
}

export const useOutsSelectionScanning = (imageId: string, retryWithoutCache: boolean) => {
  const { token } = useAuthContext();
  const { productRecognitionData, isRunningModel, runProductRecognitionModel } =
    useProductRecognition();

  const [cropScans, setCropScans] = useState<ProductRecognitionCropScanResponse[]>([]);
  const [cropScansError, setCropScansError] = useState<Error | null>(null);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [isSavingEmbeddings, setIsSavingEmbeddings] = useState(false);

  // Map product recognition data to crop scans whenever it changes
  useEffect(() => {
    if (!productRecognitionData) return;

    const mapped = [
      ...(productRecognitionData.productBoxes ?? []).map((bbox, i) =>
        mapBBoxToCropScan(bbox, i, 'product', 0)
      ),
      ...(productRecognitionData.cartonBoxes ?? []).map((bbox, i) =>
        mapBBoxToCropScan(bbox, i, 'carton', 10000)
      ),
    ];

    setCropScans(mapped);
  }, [productRecognitionData]);

  const startSession = useCallback(async (): Promise<ProductRecognitionCropScanResponse[]> => {
    if (!imageId) return [];

    setCropScansError(null);
    setSessionStarted(true);

    try {
      await runProductRecognitionModel(Number(imageId), {
        useApiCache: true,
        retryWithoutCache,
      });
      // cropScans will be set via the useEffect above when productRecognitionData updates
      return [];
    } catch (error) {
      setCropScansError(
        error instanceof Error ? error : new Error('Failed to load product recognition')
      );
      return [];
    }
  }, [imageId, retryWithoutCache, runProductRecognitionModel]);

  const createEmbeddings = useCallback(
    async (updates: OutsSelectionEmbeddingUpdate[]): Promise<boolean> => {
      if (!imageId || !token || updates.length === 0) return false;

      setIsSavingEmbeddings(true);
      setCropScansError(null);

      try {
        await ApiService.createEmbeddingFromBbox(token, {
          imageId: Number(imageId),
          crops: updates.map((update) => ({
            barcode: update.barcode,
            normalizedUpc: update.normalizedUpc,
            bbox: [...update.bbox],
            productId: update.productId,
          })),
          source: 'control',
          isActive: false,
          isPending: true,
        });
        return true;
      } catch (error) {
        setCropScansError(
          error instanceof Error ? error : new Error('Failed to create embeddings')
        );
        return false;
      } finally {
        setIsSavingEmbeddings(false);
      }
    },
    [imageId, token]
  );

  return {
    createEmbeddings,
    cropScansError,
    cropScans,
    productRecognitionData,
    isRunningModel,
    isFetchingCropScans: isRunningModel || isSavingEmbeddings,
    isGeneratingCropScans: false,
    sessionStarted,
    startSession,
    setCropScans,
  };
};
